import type { ResidentReportModel } from "../types/resident-report.types";
import "./ResidentReport.css";
import { EventParticipation } from "./EventParticipation";
import { InterestParticipation } from "./InterestParticipation";
import { MetricsGrid } from "./MetricsGrid";
import { MonthlyActivityChart } from "./MonthlyActivityChart";
import { RecentEngagement } from "./RecentEngagement";
import { ResidentProfile } from "./ResidentProfile";

export interface ResidentReportProps {
  data: ResidentReportModel;
}

export function ResidentReport({ data }: ResidentReportProps) {
  return (
    <article
      className="resident-report-page"
      aria-label={`Resident report for ${data.resident.name}`}
    >
      <div className="resident-report-page__top">
        <ResidentProfile
          resident={data.resident}
          engagement={data.engagement}
        />
      </div>
      <MetricsGrid metrics={data.metrics} />
      <div className="resident-report-page__main">
        <EventParticipation events={data.events} />
        <RecentEngagement recentEngagement={data.recentEngagement} />
      </div>
      <div className="resident-report-page__bottom">
        <MonthlyActivityChart
          activityByMonth={data.activityByMonth}
          activitySummary={data.activitySummary}
        />
        <InterestParticipation
          interests={data.interests}
          participationGap={data.participationGap}
        />
      </div>
      {(data.residentVoice || data.accessPreferences || data.nextBestAction) && (
        <div className="resident-report-page__insights">
          {data.residentVoice && (
            <section className="report-card resident-voice">
              <p className="report-eyebrow">Resident voice</p>
              <blockquote>“{data.residentVoice.quote}”</blockquote>
              <p>{data.residentVoice.source}</p>
              <div className="resident-tags">
                {data.residentVoice.themes.map((theme) => (
                  <span key={theme}>{theme}</span>
                ))}
              </div>
            </section>
          )}
          {data.accessPreferences && (
            <section className="report-card">
              <p className="report-eyebrow">Access &amp; preferences</p>
              <h2>How to make participation easier</h2>
              <dl className="resident-preferences">
                <div>
                  <dt>Preferred channel</dt>
                  <dd>{data.accessPreferences.preferredChannel}</dd>
                </div>
                <div>
                  <dt>Best time</dt>
                  <dd>{data.accessPreferences.preferredTime}</dd>
                </div>
                <div>
                  <dt>Language</dt>
                  <dd>{data.accessPreferences.language}</dd>
                </div>
                <div>
                  <dt>Access needs</dt>
                  <dd>{data.accessPreferences.accessNeeds.join(" · ")}</dd>
                </div>
              </dl>
            </section>
          )}
          {data.nextBestAction && (
            <section className="report-card resident-next-action">
              <p className="report-eyebrow">Signals &amp; next best action</p>
              <h2>{data.nextBestAction.title}</h2>
              <p>{data.nextBestAction.detail}</p>
              <div>
                <span>Owner: {data.nextBestAction.owner}</span>
                <span>Timing: {data.nextBestAction.timing}</span>
              </div>
            </section>
          )}
        </div>
      )}
    </article>
  );
}
